import { limitPct, money } from '../format';
import type { KeyOut } from '../types';

/**
 * limit_usd null (no limit) and 0 (blocks everything) are different facts; rendering
 * null as $0 would be a lie, so they get different text and no bar for null.
 */
export function LimitCell({ keyRow }: { keyRow: KeyOut }) {
  const { limit_usd: limit, limit_remaining: remaining } = keyRow;

  if (limit === null) {
    return (
      <div className="limit">
        <span className="muted" title="No per-key limit is set upstream; spend is uncapped by this key.">
          No limit set
        </span>
      </div>
    );
  }

  const used = remaining === null ? null : limit - remaining;
  const pct = limitPct(used, limit);
  const exhausted = remaining !== null && remaining <= 0;

  return (
    <div className="limit">
      <div className="limit-figures">
        <span className="limit-cap">{money(limit)}</span>
        <span className={exhausted ? 'limit-remaining exhausted' : 'limit-remaining'}>
          {remaining === null ? 'remaining unknown' : `${money(remaining)} left`}
        </span>
      </div>
      {pct === null ? null : (
        <div
          className="meter"
          role="meter"
          aria-valuenow={Math.round(pct)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`Limit consumed for ${keyRow.name}`}
          title={`${money(used)} of ${money(limit)} used this period (approximate: enforcement uses a separate upstream pipeline).`}
        >
          <div
            className={`meter-fill ${pct >= 100 ? 'meter-full' : pct >= 80 ? 'meter-high' : ''}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      )}
      {limit === 0 ? <span className="limit-note warn">$0 limit blocks all requests</span> : null}
      {keyRow.limit_reset ? (
        <span className="limit-note muted">resets {keyRow.limit_reset}</span>
      ) : (
        <span className="limit-note muted">no reset (lifetime limit)</span>
      )}
    </div>
  );
}
